/**
 * Notes row displayed between calendar header and time grid
 * Shows todo items for each day in the calendar view
 * Uses a portal to inject into the calendar DOM structure
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Plus } from 'lucide-react'
import { NoteItem } from './NoteItem'
import { NoteDialog } from '@/components/dialogs/NoteDialog'
import { useCalendarNotes, useNoteMutations } from '@/hooks/useCalendarNotes'
import { getWeekDates, formatDateForApi } from '@/lib/calendar-utils'
import type { CalendarNote, AppointmentType, CalendarView } from '@/types/calendar'

interface CalendarNotesRowProps {
	currentDate: Date
	view: CalendarView
	calendarType: AppointmentType
}

export function CalendarNotesRow({
	currentDate,
	view,
	calendarType,
}: CalendarNotesRowProps) {
	const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
	const [gutterWidth, setGutterWidth] = useState<number>(72)
	const [dialogOpen, setDialogOpen] = useState(false)
	const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
	const [selectedNote, setSelectedNote] = useState<CalendarNote | null>(null)
	const [selectedDate, setSelectedDate] = useState<Date>(currentDate)
	const observerRef = useRef<MutationObserver | null>(null)

	// Find and set up portal target
	useEffect(() => {
		const findAndSetTarget = () => {
			const timeView = document.querySelector('.rbc-time-view')
			const timeHeader = document.querySelector('.rbc-time-header')
			const timeContent = document.querySelector('.rbc-time-content')
			const timeGutter = document.querySelector('.rbc-time-gutter')

			if (timeView && timeHeader && timeContent) {
				// Get the actual gutter width
				if (timeGutter) {
					const width = timeGutter.getBoundingClientRect().width
					setGutterWidth(width)
				}

				// Check if our container already exists
				let notesContainer = timeView.querySelector('.calendar-notes-container')
				if (!notesContainer) {
					// Create a container for our notes row
					notesContainer = document.createElement('div')
					notesContainer.className = 'calendar-notes-container'
					// Insert after the header, before the time content
					timeView.insertBefore(notesContainer, timeContent)
				}
				setPortalTarget(notesContainer as HTMLElement)
			}
		}

		// Initial attempt
		findAndSetTarget()

		// Set up observer to watch for calendar DOM changes
		observerRef.current = new MutationObserver(() => {
			findAndSetTarget()
		})

		const calendarContainer = document.querySelector('.calendar-with-notes')
		if (calendarContainer) {
			observerRef.current.observe(calendarContainer, {
				childList: true,
				subtree: true,
			})
		}

		return () => {
			observerRef.current?.disconnect()
		}
	}, [view, currentDate])

	// Get dates to display based on view
	const dates = useMemo(() => {
		if (view === 'day') {
			return [currentDate]
		}
		const weekDates = getWeekDates(currentDate)
		if (view === 'work_week') {
			return weekDates.slice(0, 5) // Monday to Friday
		}
		return weekDates // Full week
	}, [currentDate, view])

	// Calculate date range for fetching notes
	const dateRange = useMemo(() => {
		const from = dates[0]
		const to = dates[dates.length - 1]
		return { from, to }
	}, [dates])

	// Fetch notes
	const { notesByDate } = useCalendarNotes({
		type: calendarType,
		from: dateRange.from,
		to: dateRange.to,
	})

	// Mutations
	const { create, update, toggle, delete: deleteNote, isToggling } = useNoteMutations()

	// Handlers
	const handleOpenCreateDialog = useCallback((date: Date) => {
		setSelectedDate(date)
		setSelectedNote(null)
		setDialogMode('create')
		setDialogOpen(true)
	}, [])

	const handleOpenEditDialog = useCallback((note: CalendarNote) => {
		setSelectedNote(note)
		setDialogMode('edit')
		setDialogOpen(true)
	}, [])

	const handleCloseDialog = useCallback(() => {
		setDialogOpen(false)
		setSelectedNote(null)
	}, [])

	const handleToggle = useCallback(
		async (id: string) => {
			await toggle({ id })
		},
		[toggle],
	)

	const handleDelete = useCallback(
		async (id: string) => {
			if (window.confirm('Opravdu chcete smazat tuto poznámku?')) {
				await deleteNote({ id })
			}
		},
		[deleteNote],
	)

	const handleCreate = useCallback(
		async (data: { date: Date; timeFrom: string; timeTo: string; text: string; type: AppointmentType; color?: string | null }) => {
			await create(data)
		},
		[create],
	)

	const handleUpdate = useCallback(
		async (data: { id: string; timeFrom?: string; timeTo?: string; text?: string; color?: string | null }) => {
			await update(data)
		},
		[update],
	)

	const notesRowContent = (
		<div className="calendar-notes-row flex border-b border-[#e8e4df] bg-[#faf8f5]">
			{/* Time gutter placeholder - dynamically matches react-big-calendar time gutter */}
			<div
				className="flex-shrink-0 border-r border-[#e8e4df] bg-[#f8f6f3]"
				style={{ width: gutterWidth }}
			/>

			{/* Notes columns for each day */}
			<div className="flex flex-1">
				{dates.map((date) => {
					const dateStr = formatDateForApi(date)
					const dayNotes = notesByDate[dateStr] || []

					const doneCount = dayNotes.filter((n) => n.isDone).length
					const totalCount = dayNotes.length

					return (
						<div
							key={dateStr}
							className="flex-1 border-r border-[#e8e4df] last:border-r-0 min-h-[60px] p-1 flex flex-col overflow-hidden"
						>
							{/* Notes list - max 3 visible, scrollable */}
							<div className="space-y-0.5 overflow-y-auto max-h-[90px] flex-1 scrollbar-thin">
								{dayNotes.map((note) => (
									<NoteItem
										key={note.id}
										note={note}
										onToggle={handleToggle}
										onEdit={handleOpenEditDialog}
										onDelete={handleDelete}
										isToggling={isToggling}
									/>
								))}
							</div>

							{/* Footer row with add button and counter */}
							<div className="mt-1 flex-shrink-0 flex items-center gap-1">
								<button
									type="button"
									onClick={() => handleOpenCreateDialog(date)}
									className="flex-1 flex items-center justify-center gap-1 py-1 px-2 rounded text-[#8c8275] hover:bg-[#f5ebe0] hover:text-[#5c5142] transition-colors"
									style={{ fontSize: 'var(--font-size-note-text)' }}
								>
									<Plus className="w-3 h-3" />
									<span>Přidat</span>
								</button>

								{totalCount > 0 && (
									<span
										className="flex-shrink-0 text-[#8c8275] px-1"
										style={{ fontSize: 'var(--font-size-note-time)' }}
									>
										{doneCount}/{totalCount}
									</span>
								)}
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)

	return (
		<>
			{/* Portal the notes row into the calendar structure */}
			{portalTarget && createPortal(notesRowContent, portalTarget)}

			{/* Note dialog - rendered normally */}
			<NoteDialog
				open={dialogOpen}
				onClose={handleCloseDialog}
				mode={dialogMode}
				note={selectedNote}
				date={selectedNote ? new Date(selectedNote.date) : selectedDate}
				calendarType={calendarType}
				onCreate={handleCreate}
				onUpdate={handleUpdate}
			/>
		</>
	)
}
